import { Download } from "lucide-react";
import Link from "next/link";

import {
  getIoFileHref,
  type IoFileLike,
} from "@/components/Spotflow/WorkflowRunner/Outputs/ioFileDisplay";
import { cn } from "@/lib/utils";

export type IoRowTruncation = {
  // Items actually received for this output (after the endpoint's list caps).
  shown: number;
  // Full item count before truncation.
  total: number;
  // Index of the first received item within the full list.
  offset?: number;
  // Full content as a downloadable JSON file, when the upload succeeded.
  file: IoFileLike | null;
};

export function ioTruncationLabel({
  shown,
  total,
  offset = 0,
}: IoRowTruncation): string {
  if (shown === 0) return "No items in this range";
  if (total <= shown) return "Truncated: long items are shortened";
  if (offset > 0)
    return `Showing items ${offset + 1}-${offset + shown} of ${total}`;
  return `Showing first ${shown} of ${total} items`;
}

/** Footer note for a list value the endpoint could not send whole, with a link
 *  to the full content it uploaded. */
export function IoTruncationNote({
  truncation,
  className,
}: {
  truncation: IoRowTruncation;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 text-xs text-muted-foreground",
        className
      )}
    >
      <span className="italic">{ioTruncationLabel(truncation)}</span>
      {truncation.file && (
        <Link
          href={getIoFileHref(truncation.file, false)}
          download
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 not-italic text-primary hover:underline"
        >
          <Download className="size-3" />
          Download full output
        </Link>
      )}
    </div>
  );
}
